import React, { useEffect, useRef, useState, useCallback } from 'react';
import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import styles from './EditorExtension_RecordBrowser.module.css';
import { useCCContext } from '../CC/CCContext';
import { CC } from '../CC/CC';
import * as ccUtils from '../CC/utils';
import { generateQuery, replaceBasketIdSubqueries, postWithCache } from '../CC/utils';
import { getImages } from '../Result/getImages';

const parseStyleString = (styleString) => {
  if (!styleString) return {};

  return styleString.split(';').reduce((styleObj, rule) => {
    const [property, value] = rule.split(':').map(item => item.trim());
    if (property && value) {
      // Convert kebab-case to camelCase
      const camelCaseProperty = property.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
      styleObj[camelCaseProperty] = value;
    }
    return styleObj;
  }, {});
};

// ImageWithFallback.jsx (Assuming it's in the same file or imported accordingly)
// ImageWithFallback.jsx

const ImageWithFallback = ({ src, alt }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setImgSrc(src);
    setLoading(true);
  }, [src]);

  const handleLoad = () => {
    setLoading(false);
  };

  const handleError = () => {
    setImgSrc(`${process.env.REACT_APP_DZI_BASE_URL}noimage.cci&height=100`);
    setLoading(false);
  };

  return (
    <div className={`${styles.imageWrapper} relative w-full h-full`}>
      {loading && (
        <div className={`${styles.spinner} absolute inset-0 flex justify-center items-center`}>
          <div className={styles.doubleBounce1}></div>
          <div className={styles.doubleBounce2}></div>
        </div>
      )}
      <img
        src={imgSrc}
        alt={alt}
        onLoad={handleLoad}
        onError={handleError}
        className={`w-full h-full object-cover object-center ${loading ? 'hidden' : 'block'}`}
      />
    </div>
  );
};

const RecordBrowser = ({ record, onPrevious, onNext, onClick, isFirst, isLast }) => {
  if (!record) {
    return <div className={styles.noRecord}>No Record Found</div>;
  }

  //console.log(getImages(record));

  const imageUrl = getImages(record)["mobilerecord"]?.[0]?.url || `${process.env.REACT_APP_DZI_BASE_URL}noimage.cci&height=640&cache=yes`;

  return (
    <div className={styles.recordBrowser}>
      <div className={styles.imageContainer} onClick={onClick}>
        <ImageWithFallback
          src={imageUrl}
        />
      </div>
      <div className={styles.recordDetails} onClick={onClick}>
        <h2 className={styles.title}>{record.data.record?.title?.title}</h2>
        {/* Add more fields here as needed */}
      </div>
      <div className={styles.navigationButtons}>
        <button onClick={onPrevious} disabled={isFirst} className={styles.navButton}>
          Previous
        </button>
        <button onClick={onNext} disabled={isLast} className={styles.navButton}>
          Next
        </button>
      </div>
    </div>
  );
};

export default Node.create({
  name: 'reactComponentRecordBrowser',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      selected: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-selected') === 'true',
        renderHTML: (attributes) => ({
          'data-selected': attributes.selected
        }),
      },
      state: {
        default: '',
        parseHTML: (element) => {
          const state = element.getAttribute('data-state');
          return state;
        },
        renderHTML: (attributes) => ({
          'data-state': attributes.state
        }),
      },
      htmlStyle: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-html-style'),
        renderHTML: (attributes) => ({
          'data-html-style': attributes.htmlStyle
        }),
      }
    }
  },

  parseHTML() {
    return [
      {
        tag: 'RecordBrowser',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return ['RecordBrowser', mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(Component);
  },
});

const Component = (props) => {
  const { setCCContext, clearSearch, setViewMode, recordsPerPage } = useCCContext();
  const { selected, updateAttributes } = props;
  const { state, htmlStyle } = props.node.attrs;
  const [record, setRecord] = useState(null);
  const componentRef = useRef({});
  const containerRef = useRef(null);
  const [currentRecordIndex, setCurrentRecordIndex] = useState(1);
  const [isFirst, setIsFirst] = useState(true);
  const [isLast, setIsLast] = useState(false);
  const { user } = useCCContext();

  const fetchRecord = useCallback(async (index) => {
    try {
      let stateCC = state ? await ccUtils.GetCCFromState(state.replace(/'/g, '"')) : new CC();
/*      let stateCC = {};
      if (state && state !== '') {
        stateCC = JSON.parse(state);
      } else {
        stateCC = new CC();
      }*/
      let query = generateQuery(stateCC, user);
      query = await replaceBasketIdSubqueries(query);
      const payload = {
        action: "get",
        command: "search",
        query: query,
        range: `${index}-${index}`,
        responseformat: "json"
      };
      const response = await postWithCache(process.env.REACT_APP_CC_URL, payload, componentRef.current);

      if (response.records && response.records.record && response.records.record.data) {
        setRecord(response.records.record);
        setIsFirst(index === 1);
        setIsLast(index >= response.request.count);
      } else {
        setRecord(null);
        setIsLast(true);
      }
    } catch (error) {
      setRecord(null);
      setIsLast(true);
      console.error("Error fetching record:", error);
    }
  }, [state, user]);

  useEffect(() => {
    fetchRecord(currentRecordIndex);
  }, [currentRecordIndex, fetchRecord]);

  useEffect(() => {
    const handleSelection = () => {
      updateAttributes({ selected: props.editor.isActive('reactComponentRecordBrowser') });
    };

    props.editor.on('selectionUpdate', handleSelection);

    return () => {
      props.editor.off('selectionUpdate', handleSelection);
    };
  }, [props.editor, updateAttributes]);

  const handleDoubleClick = (event) => {
    if (props.editor.isEditable) {
      event.stopPropagation();
      const { state, query, htmlStyle} = props.node.attrs;
      const customEvent = new CustomEvent('showInsertContentPopup', {
        detail: {
          extension: 'RecordBrowser',
          props: { state, query, htmlStyle},
          pos: props.getPos(),
          node: props.node
        },
        bubbles: true,
        cancelable: true
      });
      event.target.dispatchEvent(customEvent);
    }
  };

  const handlePrevious = () => {
    if (currentRecordIndex > 1) {
      setCurrentRecordIndex(currentRecordIndex - 1);
    }
  };

  const handleNext = () => {
    setCurrentRecordIndex(currentRecordIndex + 1);
  };

  const handleClick = () => {
    if (!props.editor.isEditable) {
      const queryParams = new URLSearchParams(window.location.search);
      if (queryParams.get('albumexternal')) {
        window.open(`${window.location.origin}${window.location.pathname}?state=${state}&index=${currentRecordIndex}`, '_blank');
      } else {
        if (state) {
          try {
            const defaultCCObj = new CC();
            const startUPCCObj = ccUtils.deepMerge(defaultCCObj, JSON.parse(state));
            setCCContext({
              cc: startUPCCObj,
              first: Math.floor((currentRecordIndex - 1) / recordsPerPage) * recordsPerPage + 1,
              selectedRecord: currentRecordIndex,
              viewMode: 'list',
            });
          } catch (error) {
            console.error("Error parsing state:", error);
          }
        } else {
          clearSearch();
          setViewMode('list');
        }
      }
    }
  };

  return (
    <NodeViewWrapper as="span" className={`react-component ${selected ? 'selected' : ''}`}>
      <span
        ref={containerRef}
        className={`${styles.recordBrowserContainer}`}
        onDoubleClick={handleDoubleClick}
      >
        <span style={parseStyleString(htmlStyle)}>
          <RecordBrowser
            record={record}
            onPrevious={handlePrevious}
            onNext={handleNext}
            isFirst={isFirst}
            isLast={isLast}
            onClick={handleClick}
          />
        </span>
      </span>
    </NodeViewWrapper>
  );
};